import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllPosts } from '../content/blog/registry';
import { useSeo } from '../lib/useSeo';

export default function BlogPage() {
  const posts = getAllPosts();

  useSeo({
    title: 'Blog - DAIEN | Praktyczne AI dla firm',
    description: 'Case studies i poradniki o wdrażaniu AI w firmach: voice agenci, RAG, cold mailing, SEO z AI. Konkrety, liczby i wnioski z realnych wdrożeń.',
    path: '/blog',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-5xl mx-auto">
        <Link to="/" className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2">
          ← DAIEN
        </Link>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="w-8 h-px bg-stroke"></span>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Blog</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light mb-6">
            Notatki <span className="font-display italic">z wdrożeń</span>
          </h1>
          <p className="text-muted text-lg max-w-2xl">
            Case studies, stack decyzje, antypatterns. Bez ściemy o "AI rewolucji" - tylko realne liczby z polskich wdrożeń.
          </p>
        </motion.div>

        <div className="space-y-2">
          {posts.length === 0 ? (
            <p className="text-muted">Pierwsze posty pojawią się wkrótce.</p>
          ) : (
            posts.map((post, i) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: "easeOut" }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col md:flex-row gap-6 p-6 md:p-8 rounded-3xl hover:bg-surface transition-colors border border-transparent hover:border-stroke"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 flex-1">
                    <time className="text-xs text-muted font-mono whitespace-nowrap md:w-24">
                      {formatDate(post.date)}
                    </time>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl md:text-2xl font-light text-text-primary group-hover:text-accent-to transition-colors mb-2 leading-snug">
                        {post.title}
                      </h2>
                      <p className="text-muted text-sm leading-relaxed mb-3 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted">
                        <span>{post.readTime} min czytania</span>
                        <span className="w-1 h-1 rounded-full bg-stroke"></span>
                        <div className="flex gap-2">
                          {post.tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-surface rounded-full text-[10px] uppercase tracking-wider">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="self-start md:self-center text-muted group-hover:text-accent-to transition-colors">
                    →
                  </div>
                </Link>
              </motion.article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
}
